import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  getRawMaterialTypes, createRawMaterialType, updateRawMaterialType, deleteRawMaterialType,
  getRawMaterialLots, createRawMaterialLot, updateRawMaterialLot, deleteRawMaterialLot,
  getSuppliers,
  getRmConfig, addRmConfigItem, renameRmConfigItem, deleteRmConfigItem,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { formatDate } from "@/lib/dates";
import { FlaskConical, Plus, Trash2, Pencil, Package, X, SlidersHorizontal, Check } from "lucide-react";

const DEFAULT_CATEGORIES = ["rennet", "culture", "salt", "additive", "other"];
const DEFAULT_UNITS = ["g", "kg", "mg", "ml", "l", "pcs"];
const FIELD_TYPES = ["text", "number", "date", "select"];

// Show a category as-is; if it matches a legacy built-in key, use its translation.
const catLabel = (t, c) => {
  if (!c) return "—";
  const key = `rawMaterials.cat.${c}`;
  const translated = t(key);
  return translated === key ? c : translated;
};

const today = () => new Date().toISOString().slice(0, 10);

const daysLeft = (expiry) => {
  if (!expiry) return null;
  try {
    const e = new Date(expiry.slice(0, 10));
    const n = new Date(today());
    return Math.round((e - n) / 86400000);
  } catch { return null; }
};

export default function RawMaterials() {
  const { t } = useT();
  const { can } = useAuth();
  const canManageTypes = can("admin", "manager");
  const [tab, setTab] = useState("lots");
  const [types, setTypes] = useState([]);
  const [lots, setLots] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [hideEmpty, setHideEmpty] = useState(true);

  const [typeDialog, setTypeDialog] = useState(null); // {} for new, object for edit
  const [lotDialog, setLotDialog] = useState(null);
  const [configDialog, setConfigDialog] = useState(false);
  const [delType, setDelType] = useState(null);
  const [delLot, setDelLot] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tp, lt, sp, cfg] = await Promise.all([
        getRawMaterialTypes(true),
        getRawMaterialLots(),
        getSuppliers({ category: "other" }).catch(() => []),
        getRmConfig().catch(() => null),
      ]);
      setTypes(tp || []);
      setLots(lt || []);
      setSuppliers(sp || []);
      if (cfg) {
        setCategories(cfg.categories?.length ? cfg.categories : DEFAULT_CATEGORIES);
        setUnits(cfg.units?.length ? cfg.units : DEFAULT_UNITS);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const typeById = (id) => types.find((x) => x.id === id);
  const filteredLots = useMemo(
    () => lots.filter((l) =>
      (filterType === "all" || l.type_id === filterType) &&
      (!hideEmpty || Number(l.remaining) > 1e-9)),
    [lots, filterType, hideEmpty]
  );

  const removeType = async () => {
    if (!delType) return;
    try {
      await deleteRawMaterialType(delType.id);
      toast.success(t("rawMaterials.typeDeleted"));
      setDelType(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };
  const removeLot = async () => {
    if (!delLot) return;
    try {
      await deleteRawMaterialLot(delLot.id);
      toast.success(t("rawMaterials.lotDeleted"));
      setDelLot(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  return (
    <Layout>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <FlaskConical className="h-6 w-6 text-[hsl(var(--brand))]" /> {t("rawMaterials.title")}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("rawMaterials.subtitle")}</p>
        </div>
        {canManageTypes && (
          <Button variant="outline" size="sm" onClick={() => setConfigDialog(true)} data-testid="rm-open-config-button">
            <SlidersHorizontal className="mr-1.5 h-4 w-4" /> {t("rawMaterials.manageConfig")}
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="rm-tabs">
          <TabsTrigger value="lots" data-testid="rm-tab-lots">{t("rawMaterials.tabLots")}</TabsTrigger>
          <TabsTrigger value="types" data-testid="rm-tab-types">{t("rawMaterials.tabTypes")}</TabsTrigger>
        </TabsList>

        {/* ---------------- LOTS ---------------- */}
        <TabsContent value="lots" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Package className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("rawMaterials.lotsTitle")} ({filteredLots.length})
                </CardTitle>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch id="rm-hide-empty" checked={hideEmpty} onCheckedChange={setHideEmpty} data-testid="rm-hide-empty-toggle" />
                    <Label htmlFor="rm-hide-empty" className="cursor-pointer text-xs text-muted-foreground">{t("rawMaterials.hideEmpty")}</Label>
                  </div>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="h-9 w-[220px]" data-testid="rm-lots-filter-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("rawMaterials.allTypes")}</SelectItem>
                      {types.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" disabled={types.length === 0}
                    onClick={() => setLotDialog({})} data-testid="rm-add-lot-button">
                    <Plus className="mr-1.5 h-4 w-4" /> {t("rawMaterials.receiveLot")}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
              ) : types.length === 0 ? (
                <div className="flex flex-col items-center py-12 text-center">
                  <FlaskConical className="h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-3 text-sm text-muted-foreground">{t("rawMaterials.noTypesFirst")}</p>
                  {canManageTypes && (
                    <Button size="sm" className="mt-4" onClick={() => { setTab("types"); setTypeDialog({}); }} data-testid="rm-create-type-empty">
                      <Plus className="mr-1.5 h-4 w-4" /> {t("rawMaterials.addType")}
                    </Button>
                  )}
                </div>
              ) : filteredLots.length === 0 ? (
                <div className="flex flex-col items-center py-12 text-center" data-testid="rm-lots-empty">
                  <Package className="h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-3 text-sm text-muted-foreground">
                    {hideEmpty && lots.length > 0 ? t("rawMaterials.allEmptyHidden") : t("rawMaterials.lotsEmpty")}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto thin-scroll">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("rawMaterials.lotCode")}</TableHead>
                        <TableHead>{t("rawMaterials.type")}</TableHead>
                        <TableHead>{t("rawMaterials.supplier")}</TableHead>
                        <TableHead>{t("rawMaterials.supplierLot")}</TableHead>
                        <TableHead className="text-right">{t("rawMaterials.remaining")}</TableHead>
                        <TableHead>{t("rawMaterials.received")}</TableHead>
                        <TableHead>{t("rawMaterials.expiry")}</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLots.map((l) => {
                        const dl = daysLeft(l.expiry_date);
                        const low = Number(l.remaining) <= 0;
                        return (
                          <TableRow key={l.id} data-testid={`rm-lot-row-${l.id}`}>
                            <TableCell className="font-mono text-xs">{l.lot_code}</TableCell>
                            <TableCell className="font-medium">{l.type_name}
                              <div className="text-[11px] text-muted-foreground">{catLabel(t, l.category)}</div>
                            </TableCell>
                            <TableCell className="text-sm">{l.supplier_name || "—"}</TableCell>
                            <TableCell className="font-mono text-xs">{l.supplier_lot_no || "—"}</TableCell>
                            <TableCell className="text-right font-mono tabular-nums">
                              <span className={low ? "text-[hsl(var(--danger))]" : ""}>{l.remaining}</span>
                              <span className="text-muted-foreground"> / {l.qty} {l.unit}</span>
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-xs">{formatDate(l.received_date, "—")}</TableCell>
                            <TableCell className="whitespace-nowrap text-xs">
                              {l.expiry_date ? (
                                <span className="inline-flex items-center gap-1.5">
                                  {formatDate(l.expiry_date)}
                                  {dl != null && dl < 0 && <Badge className="badge-bad">{t("rawMaterials.expired")}</Badge>}
                                  {dl != null && dl >= 0 && dl <= 30 && <Badge className="badge-warn">{dl}d</Badge>}
                                </span>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <Button variant="ghost" size="icon" title={t("common.edit")} onClick={() => setLotDialog(l)} data-testid={`rm-lot-edit-${l.id}`}>
                                <Pencil className="h-4 w-4 text-[hsl(var(--brand))]" />
                              </Button>
                              <Button variant="ghost" size="icon" title={t("common.delete")} onClick={() => setDelLot(l)} data-testid={`rm-lot-delete-${l.id}`}>
                                <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- TYPES ---------------- */}
        <TabsContent value="types" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FlaskConical className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("rawMaterials.typesTitle")} ({types.length})
                </CardTitle>
                {canManageTypes && (
                  <Button size="sm" onClick={() => setTypeDialog({})} data-testid="rm-add-type-button">
                    <Plus className="mr-1.5 h-4 w-4" /> {t("rawMaterials.addType")}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
              ) : types.length === 0 ? (
                <div className="flex flex-col items-center py-12 text-center" data-testid="rm-types-empty">
                  <FlaskConical className="h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-3 text-sm text-muted-foreground">{t("rawMaterials.typesEmpty")}</p>
                </div>
              ) : (
                <div className="overflow-x-auto thin-scroll">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("rawMaterials.name")}</TableHead>
                        <TableHead>{t("rawMaterials.category")}</TableHead>
                        <TableHead>{t("rawMaterials.defaultUnit")}</TableHead>
                        <TableHead>{t("rawMaterials.tracksExpiry")}</TableHead>
                        <TableHead>{t("rawMaterials.customFields")}</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {types.map((x) => (
                        <TableRow key={x.id} data-testid={`rm-type-row-${x.id}`} className={x.active === false ? "opacity-60" : undefined}>
                          <TableCell className="font-medium">{x.name}
                            {x.active === false && <Badge className="ml-2 border border-border bg-secondary text-muted-foreground">{t("rawMaterials.inactive")}</Badge>}
                          </TableCell>
                          <TableCell><Badge variant="outline">{catLabel(t, x.category)}</Badge></TableCell>
                          <TableCell className="font-mono text-xs">{x.default_unit}</TableCell>
                          <TableCell>{x.tracks_expiry ? t("common.yes") : t("common.no")}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{(x.fields || []).map((f) => f.label).join(", ") || "—"}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {canManageTypes && (
                              <>
                                <Button variant="ghost" size="icon" title={t("common.edit")} onClick={() => setTypeDialog(x)} data-testid={`rm-type-edit-${x.id}`}>
                                  <Pencil className="h-4 w-4 text-[hsl(var(--brand))]" />
                                </Button>
                                <Button variant="ghost" size="icon" title={t("common.delete")} onClick={() => setDelType(x)} data-testid={`rm-type-delete-${x.id}`}>
                                  <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {typeDialog && (
        <TypeDialog type={typeDialog} categories={categories} units={units} onClose={() => setTypeDialog(null)} onSaved={() => { setTypeDialog(null); load(); }} />
      )}
      {lotDialog && (
        <LotDialog lot={lotDialog} types={types} suppliers={suppliers} units={units} onClose={() => setLotDialog(null)} onSaved={() => { setLotDialog(null); load(); }} />
      )}
      {configDialog && (
        <ConfigDialog categories={categories} units={units}
          onClose={() => setConfigDialog(false)}
          onChanged={(cfg) => { if (cfg) { setCategories(cfg.categories || []); setUnits(cfg.units || []); } load(); }} />
      )}

      <AlertDialog open={!!delType} onOpenChange={(o) => !o && setDelType(null)}>
        <AlertDialogContent data-testid="rm-type-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("rawMaterials.deleteTypeTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("rawMaterials.deleteTypeDesc", { name: delType?.name || "" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={removeType} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="rm-type-delete-confirm">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delLot} onOpenChange={(o) => !o && setDelLot(null)}>
        <AlertDialogContent data-testid="rm-lot-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("rawMaterials.deleteLotTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("rawMaterials.deleteLotDesc", { code: delLot?.lot_code || "" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={removeLot} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="rm-lot-delete-confirm">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function TypeDialog({ type, categories, units, onClose, onSaved }) {
  const { t } = useT();
  const editing = !!type?.id;
  const [name, setName] = useState(type?.name || "");
  const [category, setCategory] = useState(type?.category || categories?.[0] || "other");
  const [unit, setUnit] = useState(type?.default_unit || units?.[0] || "g");
  const [tracksExpiry, setTracksExpiry] = useState(type?.tracks_expiry !== false);
  const [active, setActive] = useState(type?.active !== false);
  const [fields, setFields] = useState(type?.fields || []);
  const [notes, setNotes] = useState(type?.notes || "");
  const [saving, setSaving] = useState(false);

  const addField = () => setFields((f) => [...f, { label: "", type: "text", unit: "", required: false }]);
  const setField = (i, patch) => setFields((f) => f.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const rmField = (i) => setFields((f) => f.filter((_, idx) => idx !== i));

  const submit = async () => {
    const nm = name.trim();
    if (!nm) { toast.error(t("rawMaterials.nameRequired")); return; }
    const cleanFields = fields
      .filter((f) => (f.label || "").trim())
      .map((f) => ({
        ...(f.id ? { id: f.id } : {}),
        label: f.label.trim(), type: f.type || "text",
        unit: (f.unit || "").trim(), required: !!f.required,
        ...(f.type === "select" ? { options: (f.options || []) } : {}),
      }));
    const body = { name: nm, category, default_unit: unit, tracks_expiry: tracksExpiry, fields: cleanFields, notes: notes.trim() };
    setSaving(true);
    try {
      if (editing) {
        await updateRawMaterialType(type.id, { ...body, active });
        toast.success(t("rawMaterials.typeUpdated"));
      } else {
        await createRawMaterialType(body);
        toast.success(t("rawMaterials.typeCreated"));
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rm-type-dialog">
        <DialogHeader><DialogTitle>{editing ? t("rawMaterials.editType") : t("rawMaterials.addType")}</DialogTitle></DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto thin-scroll pr-1">
          <div className="space-y-1.5">
            <Label>{t("rawMaterials.name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("rawMaterials.namePlaceholder")} data-testid="rm-type-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("rawMaterials.category")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="rm-type-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{catLabel(t, c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("rawMaterials.defaultUnit")}</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid="rm-type-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <Label className="text-sm">{t("rawMaterials.tracksExpiry")}</Label>
              <p className="text-[11px] text-muted-foreground">{t("rawMaterials.tracksExpiryHint")}</p>
            </div>
            <Switch checked={tracksExpiry} onCheckedChange={setTracksExpiry} data-testid="rm-type-tracks-expiry" />
          </div>
          {editing && (
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label className="text-sm">{t("rawMaterials.activeLabel")}</Label>
              <Switch checked={active} onCheckedChange={setActive} data-testid="rm-type-active" />
            </div>
          )}
          <div className="space-y-2 rounded-lg border border-border p-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">{t("rawMaterials.customFields")}</Label>
              <Button type="button" size="sm" variant="outline" onClick={addField} data-testid="rm-type-add-field">
                <Plus className="mr-1 h-3.5 w-3.5" /> {t("rawMaterials.addField")}
              </Button>
            </div>
            {fields.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">{t("rawMaterials.customFieldsHint")}</p>
            ) : fields.map((f, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`rm-type-field-${i}`}>
                <Input className="h-8 flex-1 text-sm" placeholder={t("rawMaterials.fieldLabel")} value={f.label} onChange={(e) => setField(i, { label: e.target.value })} data-testid={`rm-field-label-${i}`} />
                <Select value={f.type} onValueChange={(v) => setField(i, { type: v })}>
                  <SelectTrigger className="h-8 w-[110px] text-xs" data-testid={`rm-field-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((ft) => <SelectItem key={ft} value={ft}>{t(`rawMaterials.ftype.${ft}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input className="h-8 w-20 text-xs" placeholder={t("rawMaterials.fieldUnit")} value={f.unit || ""} onChange={(e) => setField(i, { unit: e.target.value })} />
                <Button type="button" variant="ghost" size="icon" onClick={() => rmField(i)} data-testid={`rm-field-remove-${i}`}>
                  <X className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label>{t("rawMaterials.notes")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="rm-type-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="rm-type-save">{saving ? t("common.saving") : (editing ? t("common.save") : t("common.create"))}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LotDialog({ lot, types, suppliers, units, onClose, onSaved }) {
  const { t } = useT();
  const editing = !!lot?.id;
  const [typeId, setTypeId] = useState(lot?.type_id || (types[0]?.id || ""));
  const selType = types.find((x) => x.id === typeId);
  const [supplierName, setSupplierName] = useState(lot?.supplier_name || "");
  const [supplierLot, setSupplierLot] = useState(lot?.supplier_lot_no || "");
  const [qty, setQty] = useState(lot?.qty ?? "");
  const [unit, setUnit] = useState(lot?.unit || selType?.default_unit || "g");
  const [received, setReceived] = useState((lot?.received_date || today()).slice(0, 10));
  const [expiry, setExpiry] = useState((lot?.expiry_date || "").slice(0, 10));
  const [customValues, setCustomValues] = useState(lot?.custom_values || {});
  const [notes, setNotes] = useState(lot?.notes || "");
  const [saving, setSaving] = useState(false);

  // When picking a type for a NEW lot, adopt its default unit.
  useEffect(() => {
    if (!editing && selType) setUnit(selType.default_unit || "g");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId]);

  const setCV = (fid, v) => setCustomValues((m) => ({ ...m, [fid]: v }));

  const submit = async () => {
    if (!typeId) { toast.error(t("rawMaterials.typeRequired")); return; }
    const q = Number(qty);
    if (!q || q <= 0) { toast.error(t("rawMaterials.qtyRequired")); return; }
    const body = {
      supplier_name: supplierName.trim(),
      supplier_lot_no: supplierLot.trim(),
      qty: q, unit,
      received_date: received || today(),
      expiry_date: selType?.tracks_expiry ? (expiry || null) : null,
      custom_values: customValues,
      notes: notes.trim(),
    };
    setSaving(true);
    try {
      if (editing) {
        await updateRawMaterialLot(lot.id, body);
        toast.success(t("rawMaterials.lotUpdated"));
      } else {
        await createRawMaterialLot({ type_id: typeId, ...body });
        toast.success(t("rawMaterials.lotCreated"));
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rm-lot-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? t("rawMaterials.editLot") : t("rawMaterials.receiveLot")}</DialogTitle>
          {editing && <DialogDescription className="font-mono text-xs">{lot.lot_code}</DialogDescription>}
        </DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto thin-scroll pr-1">
          <div className="space-y-1.5">
            <Label>{t("rawMaterials.type")}</Label>
            <Select value={typeId} onValueChange={setTypeId} disabled={editing}>
              <SelectTrigger data-testid="rm-lot-type"><SelectValue placeholder={t("rawMaterials.pickType")} /></SelectTrigger>
              <SelectContent>
                {types.filter((x) => x.active !== false || x.id === typeId).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("rawMaterials.quantity")}</Label>
              <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0" data-testid="rm-lot-qty" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("rawMaterials.unit")}</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid="rm-lot-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("rawMaterials.supplier")}</Label>
              <Input list="rm-suppliers" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder={t("rawMaterials.supplierPlaceholder")} data-testid="rm-lot-supplier" />
              <datalist id="rm-suppliers">
                {(suppliers || []).map((s) => <option key={s.id} value={s.name} />)}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label>{t("rawMaterials.supplierLot")}</Label>
              <Input value={supplierLot} onChange={(e) => setSupplierLot(e.target.value)} className="font-mono" data-testid="rm-lot-supplier-lot" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("rawMaterials.received")}</Label>
              <Input type="date" value={received} onChange={(e) => setReceived(e.target.value)} data-testid="rm-lot-received" />
            </div>
            {selType?.tracks_expiry && (
              <div className="space-y-1.5">
                <Label>{t("rawMaterials.expiry")}</Label>
                <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="rm-lot-expiry" />
              </div>
            )}
          </div>
          {(selType?.fields || []).length > 0 && (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <p className="text-[11px] font-medium text-muted-foreground">{t("rawMaterials.customFields")}</p>
              {(selType.fields || []).map((f) => (
                <div key={f.id} className="space-y-1">
                  <Label className="text-xs">{f.label}{f.unit ? ` (${f.unit})` : ""}</Label>
                  {f.type === "select" ? (
                    <Select value={customValues[f.id] || ""} onValueChange={(v) => setCV(f.id, v)}>
                      <SelectTrigger className="h-8" data-testid={`rm-lot-cv-${f.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.type === "number" ? "number" : (f.type === "date" ? "date" : "text")}
                      value={customValues[f.id] || ""} onChange={(e) => setCV(f.id, e.target.value)}
                      className="h-8" data-testid={`rm-lot-cv-${f.id}`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t("rawMaterials.notes")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="rm-lot-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="rm-lot-save">{saving ? t("common.saving") : (editing ? t("common.save") : t("common.create"))}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigList({ kind, items, onChanged }) {
  const { t } = useT();
  const isCat = kind === "categories";
  const [adding, setAdding] = useState("");
  const [editKey, setEditKey] = useState(null);   // the value currently being renamed
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const add = async () => {
    const v = adding.trim();
    if (!v) return;
    setBusy(true);
    try {
      const cfg = await addRmConfigItem(kind, v);
      setAdding("");
      onChanged(cfg);
      toast.success(t("rawMaterials.cfgAdded"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  const saveRename = async () => {
    const nv = editVal.trim();
    if (!nv || nv === editKey) { setEditKey(null); return; }
    setBusy(true);
    try {
      const cfg = await renameRmConfigItem(kind, editKey, nv);
      setEditKey(null);
      onChanged(cfg);
      toast.success(t("rawMaterials.cfgRenamed"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      const cfg = await deleteRmConfigItem(kind, confirmDel);
      setConfirmDel(null);
      onChanged(cfg);
      toast.success(t("rawMaterials.cfgDeleted"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder={isCat ? t("rawMaterials.cfgAddCategoryPh") : t("rawMaterials.cfgAddUnitPh")}
          data-testid={`rm-cfg-${kind}-add-input`}
        />
        <Button size="sm" onClick={add} disabled={busy || !adding.trim()} data-testid={`rm-cfg-${kind}-add-button`}>
          <Plus className="mr-1 h-4 w-4" /> {t("rawMaterials.cfgAdd")}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t("rawMaterials.cfgEmpty")}</p>
      ) : (
        <div className="max-h-[45vh] space-y-1.5 overflow-y-auto thin-scroll pr-1">
          {items.map((it) => (
            <div key={it} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5" data-testid={`rm-cfg-${kind}-row-${it}`}>
              {editKey === it ? (
                <>
                  <Input
                    autoFocus
                    className="h-8 flex-1 text-sm"
                    value={editVal}
                    onChange={(e) => setEditVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setEditKey(null); }}
                    data-testid={`rm-cfg-${kind}-edit-input`}
                  />
                  <Button variant="ghost" size="icon" onClick={saveRename} disabled={busy} title={t("common.save")} data-testid={`rm-cfg-${kind}-save-${it}`}>
                    <Check className="h-4 w-4 text-[hsl(var(--brand))]" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setEditKey(null)} title={t("common.cancel")}>
                    <X className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm">{isCat ? catLabel(t, it) : it}</span>
                  <Button variant="ghost" size="icon" title={t("common.edit")}
                    onClick={() => { setEditKey(it); setEditVal(it); }}
                    data-testid={`rm-cfg-${kind}-edit-${it}`}>
                    <Pencil className="h-4 w-4 text-[hsl(var(--brand))]" />
                  </Button>
                  <Button variant="ghost" size="icon" title={t("common.delete")}
                    onClick={() => setConfirmDel(it)}
                    data-testid={`rm-cfg-${kind}-delete-${it}`}>
                    <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid={`rm-cfg-${kind}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("rawMaterials.cfgDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("rawMaterials.cfgDeleteDesc", { name: isCat ? catLabel(t, confirmDel || "") : (confirmDel || "") })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid={`rm-cfg-${kind}-delete-confirm`}>{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ConfigDialog({ categories, units, onClose, onChanged }) {
  const { t } = useT();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rm-config-dialog">
        <DialogHeader>
          <DialogTitle>{t("rawMaterials.manageConfig")}</DialogTitle>
          <DialogDescription>{t("rawMaterials.cfgSubtitle")}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="categories">
          <TabsList data-testid="rm-config-tabs">
            <TabsTrigger value="categories" data-testid="rm-config-tab-categories">{t("rawMaterials.category")}</TabsTrigger>
            <TabsTrigger value="units" data-testid="rm-config-tab-units">{t("rawMaterials.defaultUnit")}</TabsTrigger>
          </TabsList>
          <TabsContent value="categories" className="mt-3">
            <ConfigList kind="categories" items={categories} onChanged={onChanged} />
          </TabsContent>
          <TabsContent value="units" className="mt-3">
            <ConfigList kind="units" items={units} onChanged={onChanged} />
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rm-config-close">{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
